import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
// Runs Amplify.configure() on module load — must precede any auth call.
import './auth/cognito.ts';
import { assertDevOrigin } from './config/assertDevOrigin.ts';
import { installPwaCapture } from './pwa/install.ts';
import { initRum } from './telemetry/rum.ts';
import './styles/tokens.css';
import './styles/global.css';

// Loud, early warning if the dev origin isn't the CORS-allowlisted one
// (no-op in production). Prevents the silent "/auth/* CORS-blocked" hang.
assertDevOrigin();

// Capture the install prompt as early as possible — the browser fires
// `beforeinstallprompt` once at load, and the onboarding tour replays it later.
installPwaCapture();

// Real-user monitoring (page views / errors / signup funnel) — consent- and
// GPC-gated inside; fire-and-forget so telemetry can never block first paint.
void initRum();

// Register the service worker that makes the site installable. Production-only:
// the worker is a no-op pass-through, but registering it in dev needlessly
// interferes with Vite's HMR. See public/sw.js for why it exists.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Best-effort: a failed SW registration must not break the app.
    });
  });
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('#root element not found');
}

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
